// Linha da lista de contatos. Avatar + nome + telefone + tags + LTV (BRL).
// SRP: só apresentação. Click via onSelect.
import { memo } from 'react';
import { Avatar } from '@/components/ui/Avatar';
import { Badge } from '@/components/ui/Badge';
import { cn } from '@/lib/utils/cn';
import { formatBRL, formatPhone } from '@/lib/utils/formatters';
import { ANON_PII_LABEL, isContactAnonymized, type Contact } from '../api';
import { TIER_META } from '../utils/tier';
import styles from './ContactRow.module.css';

interface Props {
  contact: Contact;
  active: boolean;
  onSelect: (id: string) => void;
}

function ContactRowBase({ contact, active, onSelect }: Props) {
  const anonymized = isContactAnonymized(contact);
  return (
    <button
      type="button"
      className={cn(styles.row, active && styles.active)}
      data-active={active || undefined}
      onClick={() => onSelect(contact.id)}
      aria-current={active ? 'true' : undefined}
      aria-label={
        anonymized
          ? `Contato anonimizado ${contact.name}`
          : `Contato ${contact.name}, telefone ${contact.phone}`
      }
    >
      <Avatar name={contact.name} tone={contact.avatarTone} size="md" />
      <div className={styles.info}>
        <div className={styles.headerLine}>
          <strong className={styles.name}>{contact.name}</strong>
          {/* Categoria antes das tags: é classificação fechada, as tags são texto
              livre. Anonimizado nunca exibe categoria (o back já devolve null). */}
          {contact.tier && !anonymized && (
            <Badge variant={TIER_META[contact.tier].variant}>
              {TIER_META[contact.tier].label}
            </Badge>
          )}
          {anonymized && <Badge variant="gray">Anonimizado</Badge>}
          <span className={styles.ltv}>{formatBRL(contact.ltvCents)}</span>
        </div>
        <span className={styles.phone}>
          {anonymized ? ANON_PII_LABEL : formatPhone(contact.phone)}
        </span>
        {contact.tags.length > 0 && (
          <div className={styles.tags}>
            {contact.tags.slice(0, 3).map((t) => (
              <Badge key={t} variant="gray">
                {t}
              </Badge>
            ))}
            {contact.tags.length > 3 && (
              <span className={styles.more}>+{contact.tags.length - 3}</span>
            )}
          </div>
        )}
      </div>
    </button>
  );
}

export const ContactRow = memo(ContactRowBase);
